/**
 * Meter Service
 * Handles meter management, readings, and consumption tracking
 */

import { api_service } from './api';
import {
  Meter,
  MeterReading,
  MeterCreateData,
  MeterUpdateData,
  ReadingCreateData,
  MeterFilters,
  MeterReadingStats,
  CreateMeterReadingPayload, UnbilledReadingForInvoicing
} from '@/types/meter_types';

// ============ TYPE DEFINITIONS ============

/**
 * Paginated response type
 */
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * Meter reading filters
 */
export interface ReadingFilters {
  meter_id?: number;
  client_id?: number;
  date_from?: string;
  date_to?: string;
  has_invoice?: boolean;
}

/**
 * Meter statistics response
 */
export interface MeterStatistics {
  total_meters: number;
  active_meters: number;
  inactive_meters: number;
  faulty_meters: number;
  readings_this_month: number;
  pending_readings: number;
}

/**
 * Reading validation response
 */
export interface ReadingValidation {
  is_valid: boolean;
  warnings: string[];
  previous_reading?: number;
  expected_range?: {
    min: number;
    max: number;
  };
}

/**
 * Meter needing reading
 */
export interface MeterDueReading {
  meter: Meter;
  last_reading_date: string | null;
  days_since_reading: number;
}

// ============ HELPER FUNCTIONS ============

/**
 * Extract array data from various response formats
 * Handles: plain arrays, paginated responses, and navigation objects
 */
function extract_array_from_response<T>(data: any): T[] {
  // If it's already an array, return it
  if (Array.isArray(data)) {
    return data;
  }
  
  // If it's a paginated response with results
  if (data && typeof data === 'object' && 'results' in data && Array.isArray(data.results)) {
    return data.results;
  }
  
  // If it's a navigation object (all values are strings), return empty array
  if (data && typeof data === 'object') {
    const values = Object.values(data);
    if (values.length > 0 && values.every(v => typeof v === 'string')) {
      console.warn('Received API navigation response instead of data');
      return [];
    }
  }
  
  // Default: return empty array
  console.warn('Unexpected response format, returning empty array');
  return [];
}

// ============ METER CRUD OPERATIONS ============

/**
 * Fetch all meters with optional filters
 * @param filters - Optional filters for meter list
 */
export const fetch_meters = async (filters?: MeterFilters): Promise<Meter[]> => {
  try {
    const response = await api_service.get('/meters/meters/', {
      params: filters
    });
    
    console.log('fetch_meters raw response:', response);
    
    const meters = extract_array_from_response<Meter>(response.results);
    console.log('fetch_meters extracted:', meters);
    
    return meters;
  } catch (error) {
    console.error('Error fetching meters:', error);
    throw error;
  }
};

/**
 * Fetch paginated meters
 * @param page - Page number
 * @param page_size - Items per page
 * @param filters - Optional filters
 */
export const get_meters = async (
  page: number = 1,
  page_size: number = 20,
  filters?: MeterFilters
): Promise<PaginatedResponse<Meter>> => {
  try {
    const response = await api_service.get('/meters/', {
      params: {
        page,
        page_size,
        ...filters
      }
    });
    
    // If response is paginated, return as-is
    if (response.data && 'results' in response.data) {
      return response.data;
    }
    
    // If response is an array, wrap it in pagination format
    if (Array.isArray(response.data)) {
      return {
        count: response.data.length,
        next: null,
        previous: null,
        results: response.data
      };
    }
    
    // Default empty response
    return {
      count: 0,
      next: null,
      previous: null,
      results: []
    };
  } catch (error) {
    console.error('Error fetching meters:', error);
    throw error;
  }
};

/**
 * Fetch single meter by ID
 * @param meter_id - Meter ID
 */
export const get_meter = async (meter_id: number): Promise<Meter> => {
  try {
    const response = await api_service.get(`/meters/${meter_id}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching meter:', error);
    throw error;
  }
};

/**
 * Create new meter
 * @param data - Meter creation data
 */
export const create_meter = async (data: MeterCreateData): Promise<Meter> => {
  try {
    const response = await api_service.post('/meters/meters/', data);
    return response.data;
  } catch (error) {
    console.error('Error creating meter:', error);
    throw error;
  }
};

/**
 * Update existing meter
 * @param meter_id - Meter ID
 * @param data - Meter update data
 */
export const update_meter = async (
  meter_id: number,
  data: MeterUpdateData
): Promise<Meter> => {
  try {
    const response = await api_service.patch(`/meters/${meter_id}/`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating meter:', error);
    throw error;
  }
};

/**
 * Delete meter
 * @param meter_id - Meter ID
 */
export const delete_meter = async (meter_id: number): Promise<void> => {
  try {
    await api_service.delete(`/meters/${meter_id}/`);
  } catch (error) {
    console.error('Error deleting meter:', error);
    throw error;
  }
};

// ============ METER READINGS ============

/**
 * Fetch meter readings for a specific meter
 * CORRECT: /api/v1/meters/{meter_id}/readings/
 */
export const fetch_meter_readings = async (
  meter_id: string | number, 
  limit?: number
): Promise<any[]> => {
  try {
    const url = limit 
      ? `/meters/readings/${meter_id}/?limit=${limit}`
      : `/meters/readings/${meter_id}/`;
    
    const response = await api_service.get(url);
    
    
    // Handle different response formats
    let data;
    
    if (response.results) {
      // Paginated response: { results: [...], count: X }
      data = response.results;
    } else if (Array.isArray(response)) {
      // Direct array response: [...]
      data = response;
    } else if (response && typeof response === 'object') {
      // Single object response: {...}
      // Wrap it in an array
      data = [response];
    } else {
      // Unexpected format
      data = [];
    }
    
    console.log("Meter readings data (processed):", data);
    console.log("Is array?", Array.isArray(data));
    console.log("Length:", data?.length);
    
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching meter readings:', error);
    throw error;
  }
};

/**
 * Fetch paginated readings with filters
 * @param page - Page number
 * @param page_size - Items per page
 * @param filters - Optional filters
 */
export const get_readings = async (
  page: number = 1,
  page_size: number = 20,
  filters?: ReadingFilters
): Promise<PaginatedResponse<MeterReading>> => {
  try {
    const response = await api_service.get('/meters/readings/', {
      params: {
        page,
        page_size,
        ...filters
      }
    });
    
    // If response is paginated, return as-is
    if (response.data && 'results' in response.data) {
      return response.data;
    }
    
    // If response is an array, wrap it
    if (Array.isArray(response.data)) {
      return {
        count: response.data.length,
        next: null,
        previous: null,
        results: response.data
      };
    }
    
    // Default empty response
    return {
      count: 0,
      next: null,
      previous: null,
      results: []
    };
  } catch (error) {
    console.error('Error fetching readings:', error);
    throw error;
  }
};

/**
 * Fetch single reading by ID
 * @param reading_id - Reading ID
 */
export const get_reading = async (reading_id: number): Promise<MeterReading> => {
  try {
    const response = await api_service.get(`/meters/readings/${reading_id}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching reading:', error);
    throw error;
  }
};

/**
 * Create new meter reading
 * @param data - Reading creation data (can be JSON or FormData with photo)
 */
export const create_meter_reading = async (
  data: CreateMeterReadingPayload | FormData
): Promise<MeterReading> => {
  try {
    // If data is FormData, set appropriate headers
    const config = data instanceof FormData
      ? {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      : {};

    const response = await api_service.post('/meters/readings/', data, config);
    return response.data;
  } catch (error) {
    console.error('Error creating meter reading:', error);
    throw error;
  }
};

/**
 * Create meter reading (legacy method for compatibility)
 * @param data - Reading creation data
 */
export const create_reading = async (
  data: ReadingCreateData
): Promise<MeterReading> => {
  try {
    const response = await api_service.post('/meters/readings/', data);
    return response.data;
  } catch (error) {
    console.error('Error creating reading:', error);
    throw error;
  }
};

/**
 * Create meter reading with photo upload
 * @param data - Reading data
 * @param photo_file - Photo file
 * @param on_progress - Progress callback
 */
export const create_reading_with_photo = async (
  data: ReadingCreateData,
  photo_file: File,
  on_progress?: (progress: number) => void
): Promise<MeterReading> => {
  try {
    const form_data = new FormData();

    // Append reading data
    form_data.append('meter', data.meter.toString());
    form_data.append('current_reading', data.current_reading.toString());
    form_data.append('reading_date', data.reading_date);
    if (data.notes) {
      form_data.append('notes', data.notes);
    }

    // Append photo
    form_data.append('photo', photo_file);

    const response = await api_service.post('/meters/readings/', form_data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progress_event) => {
        if (progress_event.total && on_progress) {
          const percentage = Math.round(
            (progress_event.loaded * 100) / progress_event.total
          );
          on_progress(percentage);
        }
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error creating reading with photo:', error);
    throw error;
  }
};

/**
 * Update existing reading
 * @param reading_id - Reading ID
 * @param data - Reading update data
 */
export const update_reading = async (
  reading_id: number,
  data: Partial<ReadingCreateData>
): Promise<MeterReading> => {
  try {
    const response = await api_service.patch(`/meters/readings/${reading_id}/`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating reading:', error);
    throw error;
  }
};

/**
 * Delete reading
 * @param reading_id - Reading ID
 */
export const delete_reading = async (reading_id: number): Promise<void> => {
  try {
    await api_service.delete(`/meters/readings/${reading_id}/`);
  } catch (error) {
    console.error('Error deleting reading:', error);
    throw error;
  }
};

// ============ METER STATISTICS ============

/**
 * Fetch meter reading statistics
 * @param meter_id - Meter ID
 * @param _date_from - Start date (optional)
 * @param _date_to - End date (optional)
 */
export const fetch_meter_reading_stats = async (
  meter_id: string | number,
  date_from?: string,  // Fixed: now truly optional
  date_to?: string     // Fixed: now truly optional
): Promise<any> => {
  try {
    let url = `/meters/meters/${meter_id}/stats/`;
    const params = new URLSearchParams();
    
    if (date_from) params.append('date_from', date_from);
    if (date_to) params.append('date_to', date_to);
    
    const query = params.toString();
    if (query) url += `?${query}`;
    
    const response = await api_service.get(url);
    
    console.log("Meter stats data:", response);
    
    return response;
  } catch (error) {
    console.error('Error fetching meter reading stats:', error);
    throw error;
  }
};

/**
 * Fetch meter consumption statistics (legacy method)
 * @param meter_id - Meter ID
 * @param date_from - Start date
 * @param date_to - End date
 */
export const get_meter_stats = async (
  meter_id: number,
  date_from?: string,
  date_to?: string
): Promise<MeterReadingStats> => {
  return fetch_meter_reading_stats(meter_id, date_from, date_to);
};

/**
 * Fetch meter history
 * @param meter_id - Meter ID
 * @param limit - Maximum number of readings
 */
export const get_meter_history = async (
  meter_id: number,
  limit?: number
): Promise<MeterReading[]> => {
  return fetch_meter_readings(meter_id, limit);
};

export const fetch_latest_reading = async (meter_id: string | number): Promise<any> => {
  try {
    const response = await api_service.get(`/meters/${meter_id}/latest_reading/`);
    return response.data || response;
  } catch (error) {
    console.error('Error fetching latest reading:', error);
    throw error;
  }
};

/**
 * Fetch overall meter statistics
 */
export const get_meters_summary = async (): Promise<MeterStatistics> => {
  try {
    const response = await api_service.get('/meters/summary/');
    return response.data;
  } catch (error) {
    console.error('Error fetching meters summary:', error);
    throw error;
  }
};

// ============ VALIDATION ============

/**
 * Validate meter number (check for duplicates)
 * @param meter_number - Meter number to validate
 * @param exclude_meter_id - Meter ID to exclude from check
 */
export const validate_meter_number = async (
  meter_number: string,
  exclude_meter_id?: number
): Promise<{ is_valid: boolean; message?: string }> => {
  try {
    const response = await api_service.get('/meters/validate-meter-number/', {
      params: {
        meter_number,
        exclude: exclude_meter_id
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error validating meter number:', error);
    throw error;
  }
};

/**
 * Validate reading value (check for anomalies)
 * @param meter_id - Meter ID
 * @param current_reading - Current reading value
 */
export const validate_reading = async (
  meter_id: number,
  current_reading: number
): Promise<ReadingValidation> => {
  try {
    const response = await api_service.post('/meters/readings/validate/', {
      meter_id,
      current_reading
    });
    return response.data;
  } catch (error) {
    console.error('Error validating reading:', error);
    throw error;
  }
};

// ============ SPECIALIZED QUERIES ============

/**
 * Get latest reading for a meter
 * @param meter_id - Meter ID
 */
export const get_latest_reading = async (
  meter_id: number
): Promise<MeterReading | null> => {
  try {
    const response = await api_service.get(`/meters/${meter_id}/latest-reading/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching latest reading:', error);
    throw error;
  }
};

/**
 * Get readings without invoices (pending billing)
 */
export const get_unbilled_readings = async (): Promise<MeterReading[]> => {
  try {
    const response = await api_service.get('/meters/readings/unbilled/');
    return extract_array_from_response<MeterReading>(response.data);
  } catch (error) {
    console.error('Error fetching unbilled readings:', error);
    throw error;
  }
};

/**
 * Get readings due for entry (meters needing readings)
 */
export const get_due_readings = async (): Promise<MeterDueReading[]> => {
  try {
    const response = await api_service.get('/meters/readings/due/');
    return extract_array_from_response<MeterDueReading>(response.data);
  } catch (error) {
    console.error('Error fetching due readings:', error);
    throw error;
  }
};

// ============ BULK OPERATIONS ============

/**
 * Bulk create readings
 * @param readings - Array of reading data
 */
export const bulk_create_readings = async (
  readings: ReadingCreateData[]
): Promise<{ created: number; failed: number; errors: any[] }> => {
  try {
    const response = await api_service.post('/meters/readings/bulk-create/', { readings });
    return response.data;
  } catch (error) {
    console.error('Error bulk creating readings:', error);
    throw error;
  }
};

// ============ IMPORT/EXPORT ============

/**
 * Export meters to CSV
 * @param filters - Optional filters for export
 */
export const export_meters_csv = async (
  filters?: MeterFilters
): Promise<Blob> => {
  try {
    const response = await api_service.get('/meters/export/csv/', {
      params: filters,
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting meters to CSV:', error);
    throw error;
  }
};

/**
 * Export meters to Excel
 * @param filters - Optional filters for export
 */
export const export_meters_excel = async (
  filters?: MeterFilters
): Promise<Blob> => {
  try {
    const response = await api_service.get('/meters/export/excel/', {
      params: filters,
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting meters to Excel:', error);
    throw error;
  }
};

/**
 * Export readings to CSV/Excel
 * @param format - Export format
 * @param filters - Optional filters
 */
export const export_readings = async (
  format: 'csv' | 'excel',
  filters?: ReadingFilters
): Promise<Blob> => {
  try {
    const response = await api_service.get(`/meters/readings/export/${format}/`, {
      params: filters,
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting readings:', error);
    throw error;
  }
};

/**
 * Import meters from CSV/Excel
 * @param file - File to import
 */
export const import_meters = async (
  file: File
): Promise<{ imported: number; failed: number; errors: any[] }> => {
  try {
    const form_data = new FormData();
    form_data.append('file', file);

    const response = await api_service.post('/meters/import/', form_data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error importing meters:', error);
    throw error;
  }
};

/**
 * Import readings from CSV/Excel
 * @param file - File to import
 */
export const import_readings = async (
  file: File
): Promise<{ imported: number; failed: number; errors: any[] }> => {
  try {
    const form_data = new FormData();
    form_data.append('file', file);

    const response = await api_service.post('/meters/readings/import/', form_data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error importing readings:', error);
    throw error;
  }
};


/**
 * Fetch unbilled readings ready for invoicing
 * This returns readings without invoices, pre-formatted for invoice creation
 */
export const fetch_unbilled_readings_for_invoicing = async (): Promise<UnbilledReadingForInvoicing[]> => {
  try {
    console.log('Fetching unbilled readings for invoicing...');
    
    const response = await api_service.get('/meters/readings/unbilled-for-invoicing/');
    
    console.log('Unbilled readings response:', response);
    
    // Handle different response formats
    if (response && typeof response === 'object') {
      // Check for 'results' in response.data or response itself
      const data = response.data || response;
      
      if (data.results && Array.isArray(data.results)) {
        console.log(`Found ${data.results.length} unbilled readings`);
        return data.results;
      }
      
      if (Array.isArray(data)) {
        console.log(`Found ${data.length} unbilled readings`);
        return data;
      }
    }
    
    console.warn('Unexpected response format for unbilled readings:', response);
    return [];
  } catch (error) {
    console.error('Error fetching unbilled readings for invoicing:', error);
    
    // Return empty array instead of throwing to prevent form from breaking
    return [];
  }
};

/**
 * Get meter summary statistics
 * GET: /api/v1/meters/meters/summary/
 */
export const fetch_meter_summary = async (): Promise<any> => {
  try {
    const response = await api_service.get('/meters/meters/summary/');
    return response.data || response;
  } catch (error) {
    console.error('Error fetching meter summary:', error);
    throw error;
  }
};
// ============ CLASS-BASED SERVICE (for backward compatibility) ============

class MeterService {
  get_meters = get_meters;
  get_meter = get_meter;
  create_meter = create_meter;
  update_meter = update_meter;
  delete_meter = delete_meter;
  get_meter_history = get_meter_history;
  get_meter_stats = get_meter_stats;
  validate_meter_number = validate_meter_number;
  get_readings = get_readings;
  get_reading = get_reading;
  create_reading = create_reading;
  create_reading_with_photo = create_reading_with_photo;
  update_reading = update_reading;
  delete_reading = delete_reading;
  get_unbilled_readings = get_unbilled_readings;
  bulk_create_readings = bulk_create_readings;
  get_latest_reading = get_latest_reading;
  validate_reading = validate_reading;
  get_due_readings = get_due_readings;
  export_readings = export_readings;
  get_meters_summary = get_meters_summary;
    fetch_unbilled_readings_for_invoicing = fetch_unbilled_readings_for_invoicing;

}

// Export singleton instance
export const meter_service = new MeterService();

// Default export
export default meter_service;